import { CSSObject, Theme } from '@mui/material/styles'

/**
 * One hover contract for the whole kit.
 *
 * Before this, hover was whatever each surface happened to reach for:
 * `background.neutral` on a list row, `action.hover` in a menu, and — wherever no
 * override existed — MUI's own `alpha(palette[color].main, hoverOpacity)`, which
 * tints by the *colour prop*. A plain `<Button>` defaults to `color="primary"`,
 * and primary is navy, so the list toolbar hovered cool blue directly above rows
 * that hovered warm sand.
 *
 * The rule: only a control that already carries a colour at rest (contained
 * button, filled or soft chip) gets a colour-derived hover. Anything transparent
 * at rest gets `action.hover`, full stop.
 */

/**
 * A hover rule plus the touch reset that has to travel with it.
 *
 * Without the reset a tap leaves the hover painted until the next tap lands
 * somewhere else. MUI writes this pair itself on Button, IconButton, MenuItem and
 * ListItemButton; a styleOverride that writes a bare `&:hover` is emitted after
 * theirs at equal specificity and silently drops the reset — which is how the app
 * grew sticky highlights on tablets.
 *
 * Gating the whole rule behind `@media (hover: hover)` instead looks tidier and is
 * wrong: it hands the touch case back to MUI, and MUI's own reset is not always
 * ours. Contained Button resets to `palette[color].main`, so the amber CTA would
 * flash navy on a tap, and Chip and TableRow ship no reset at all.
 *
 * `resting` names the values to restore, so it has to cover every property
 * `hovered` changes. It defaults to the common case: transparent at rest.
 */
export function onHover(hovered: CSSObject, resting: CSSObject = { backgroundColor: 'transparent' }): CSSObject {
  return {
    '&:hover': {
      ...hovered,
      '@media (hover: none)': resting,
    },
  }
}

/**
 * The transition every hover shares, at the 120ms micro step.
 *
 * Belongs on the resting rule rather than inside `:hover`, so leaving animates at
 * the same rate as entering instead of snapping back.
 */
export function hoverTransition(theme: Theme): CSSObject {
  return {
    transition: theme.transitions.create(['background-color', 'border-color', 'color'], {
      duration: theme.transitions.duration.shortest,
    }),
  }
}

/**
 * A tinted surface — a selected row, a selected menu item — with the wash laid
 * *over* the tint rather than replacing it.
 *
 * Setting `backgroundColor` alone swapped the selection out for the wash, so a
 * hovered selected row read as no longer selected. The gradient is a flat fill and
 * is only here because `background-image` is the one paint that composites on top
 * of `background-color`.
 */
export function tintWashed(theme: Theme, tint: string): CSSObject {
  const wash = theme.palette.action.hover
  return { backgroundColor: tint, backgroundImage: `linear-gradient(${wash}, ${wash})` }
}

/** The same tint with the wash off — the resting state, and the touch reset. */
export function tintPlain(tint: string): CSSObject {
  return { backgroundColor: tint, backgroundImage: 'none' }
}

/** The common case of the two above: hover on a tinted surface, nothing nested. */
export function onHoverOverTint(theme: Theme, tint: string): CSSObject {
  return onHover(tintWashed(theme, tint), tintPlain(tint))
}
